import React, { useEffect, useState, useRef } from "react";
import {
  GetReceiptVoucherList,
  ReceiptVoucherDelete,
  ApproveReceiptVoucher,
  ExportReceiptVoucherExcel,
} from "../../../Services/Services";
import {
  Table,
  TableBody,
  Box,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Popover,
  Typography,
  Checkbox,
  Chip,
  Menu,
  Divider,
  Grid,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Edit,
  Delete,
  Print,
  Visibility,
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generateReceiptPdf } from "./ReceiptVoucherPrint";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { CircularProgress } from "@mui/material";

const ReceiptVoucher = () => {
  // ===== States =====
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedReceipt, setExpandedReceipt] = useState(null);
  const [receiptVoucher, setReceiptVoucher] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [voucherToDelete, setVoucherToDelete] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [PurchaseToApprove, setPurchaseToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [showRemarkForIndentId, setShowRemarkForIndentId] = useState(null);
  const [loading, setLoading] = useState(false); // For scroll pagination
const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  // 🆕 Date Range States
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null);
  const dateInputRef = useRef(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const [receiptDateMenuAnchor, setReceiptDateMenuAnchor] = useState(null);
  const [receiptDateRange, setReceiptDateRange] = useState({
    from: "",
    to: "",
  });
  const [tempPaymentDateRange, setTempReceiptDateRange] = useState({
    from: "",
    to: "",
  });

  const [search, setSearch] = useState({
    receipt_number: "",
    project: "",
    pi_no: "",
    sales_number: "",
    date_range: "today", // 🆕 Added
    invoice_date: "",
    party: "",
    receipt_date_from: "",
    receipt_date_to: "",
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    setIsExporting(true);

    try {
      const params = { ...search };

      if (
        search.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      if (search.receipt_date_from && search.receipt_date_to) {
        params.receipt_date_from = search.receipt_date_from;
        params.receipt_date_to = search.receipt_date_to;
      }

      const blob = await ExportReceiptVoucherExcel(params);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;

      const currentDate = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `Receipt_Vouchers_${currentDate}.xlsx`);

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Receipt Vouchers exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      if (error.response?.status === 404) {
        toast.error("No data found to export");
      } else {
        toast.error("Failed to export Excel file.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // 🆕 Get date range display text
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVouchers(1, true, search);
    }, 500);

    return () => clearTimeout(handler);
  }, [search, customDateRange, receiptDateRange]); // 🆕 Added customDateRange

  const fetchVouchers = async (
    pageNum = 1,
    reset = false,
    filterValues = search
  ) => {
    try {
    // ✅ Use filterLoading for reset/filter changes, loading for scroll
    if (reset) {
      setFilterLoading(true);
    } else {
      setLoading(true);
    }
    const params = { ...filterValues };

    // Add custom dates if custom range selected
    if (
      filterValues.date_range === "custom" &&
      customDateRange.from &&
      customDateRange.to
    ) {
      params.from_date = customDateRange.from;
      params.to_date = customDateRange.to;
    }
    if (filterValues.receipt_date_from && filterValues.receipt_date_to) {
      params.receipt_date_from = filterValues.receipt_date_from;
      params.receipt_date_to = filterValues.receipt_date_to;
    }

    const data = await GetReceiptVoucherList(params, pageNum);

    if (reset || pageNum === 1) {
      setReceiptVoucher(
        Array.isArray(data) ? data.results : data.results || []
      );
    } else {
      setReceiptVoucher((prev) => [
        ...prev,
        ...(Array.isArray(data) ? data.results : data.results || []),
      ]);
    }

    setHasMore(Array.isArray(data) ? data.next !== null : data.next !== null);
    setPage(pageNum);
  }catch (error) {
    console.error("Failed to fetch vouchers:", error);
    toast.error("Failed to load vouchers");
  } finally {
    // ✅ Reset the appropriate loading state
    setFilterLoading(false);
    setLoading(false);
  }
};

  const fetchMoreData = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVouchers(nextPage, false, search);
  };

  const handleEditClick = (voucherId) => {
    navigate(`/dashboard/receipt-voucher-create`, {
      state: {
        editMode: true,
        voucherId: voucherId,
      },
    });
  };

  const handleReset = () => {
    setSearch({
      receipt_number: "",
      project: "",
      pi_no: "",
      sales_number: "",
      date_range: "today", // 🆕 Reset to today
      invoice_date: "",
      party: "",
      receipt_date_from: "",
      receipt_date_to: "",
    });
    setCustomDateRange({ from: "", to: "" }); // 🆕 Reset custom dates
    setSelectingDate(null); // 🆕
    setReceiptVoucher([]);
    setPage(1);
    setHasMore(true);
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setReceiptDateRange({ from: "", to: "" });
    setTempReceiptDateRange({ from: "", to: "" });
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setSearch((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleDelete = async (voucherId) => {
    try {
      await ReceiptVoucherDelete(voucherId); // DELETE API

      // ✅ Correct way - use fetchVouchers with current filters and reset to page 1
      await fetchVouchers(1, true, search);

      toast.success("Deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Delete failed.");
    }
  };

  //===================search=============================
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApprove = async (voucher) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveReceiptVoucher(
        voucher.receipt_voucher_no, // <-- important change
        approvedById,
        voucher.status,
        remarkInput
      );

      setReceiptVoucher((prev) =>
        prev.map((item) =>
          item.receipt_voucher_no === updated.receipt_voucher_no
            ? updated
            : item
        )
      );

      toast.success("Purchase Voucher approved successfully!");
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error("Failed to approve Purchase Voucher.");
    }
  };

  const userRole = localStorage.getItem("role"); // "admin" ya "staff"

  const handleApplyReceiptDateRange = () => {
    setReceiptDateRange(tempPaymentDateRange);
    setSearch((prev) => ({
      ...prev,
      receipt_date_from: tempPaymentDateRange.from,
      receipt_date_to: tempPaymentDateRange.to,
      date_range: "all",
    }));
    setReceiptDateMenuAnchor(null);
  };

  // Handle Cancel
  const handleCancelReceiptDateRange = () => {
    setTempReceiptDateRange(receiptDateRange);
    setReceiptDateMenuAnchor(null);
  };

  // Handle Clear
  const handleClearReceiptDateRange = () => {
    setReceiptDateRange({ from: "", to: "" });
    setTempReceiptDateRange({ from: "", to: "" });
    setSearch((prev) => ({
      ...prev,
      receipt_date_from: "",
      receipt_date_to: "",
    }));
    setReceiptDateMenuAnchor(null);
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  return (
    <>
      <ToastContainer />

      {/* Filter Section */}
      <Box sx={{ mt: -5, pt: -3, mb: 2, width: "100%" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              label="Date Range"
              value={
                search.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(
                      customDateRange.from
                    )} to ${formatDateDisplay(customDateRange.to)}`
                  : search.date_range === "today"
                  ? "Today"
                  : search.date_range === "yesterday"
                  ? "Yesterday"
                  : search.date_range === "this_week"
                  ? "This Week"
                  : search.date_range === "previous_week"
                  ? "Previous Week"
                  : search.date_range === "this_month"
                  ? "This Month"
                  : search.date_range === "current_year"
                  ? "Current Year"
                  : search.date_range === "financial_year"
                  ? "Financial Year"
                  : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* Debtor */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              label="Debtor"
              name="party"
              size="small"
              value={search.party}
              onChange={handleSearchChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* Project */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              label="Project"
              name="project"
              size="small"
              value={search.project}
              onChange={handleSearchChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* Invoice Date */}
          <Grid item xs={12} sm={1.8}>
            <TextField
              label="Invoice Date"
              name="invoice_date"
              type="date"
              value={search.invoice_date}
              onChange={handleSearchChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={1.8}>
            <TextField
              label="Receipt Date"
              value={
                receiptDateRange.from && receiptDateRange.to
                  ? `${formatDateDisplay(
                      receiptDateRange.from
                    )} to ${formatDateDisplay(receiptDateRange.to)}`
                  : receiptDateRange.from
                  ? `From ${formatDateDisplay(receiptDateRange.from)}`
                  : receiptDateRange.to
                  ? `To ${formatDateDisplay(receiptDateRange.to)}`
                  : ""
              }
              placeholder="Select Receipt Date Range"
              onClick={(e) => {
                setReceiptDateMenuAnchor(e.currentTarget);
                setTempReceiptDateRange(receiptDateRange);
              }}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: "1 1 100px",
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.65rem",
                },
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                },
              }}
            />
          </Grid>

          <Menu
            anchorEl={receiptDateMenuAnchor}
            open={Boolean(receiptDateMenuAnchor)}
            onClose={handleCancelReceiptDateRange}
            PaperProps={{
              sx: {
                width: 320,
                p: 0,
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#fafafa",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
              >
                Select Receipt Date Range
              </Typography>

              {/* From and To Date Inputs */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempPaymentDateRange.from}
                  onChange={(e) =>
                    setTempReceiptDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempPaymentDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempPaymentDateRange.to}
                  onChange={(e) =>
                    setTempReceiptDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempPaymentDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempPaymentDateRange.from && tempPaymentDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 2,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempPaymentDateRange.from)} to{" "}
                  {formatDateDisplay(tempPaymentDateRange.to)}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={handleClearReceiptDateRange}
                  disabled={!receiptDateRange.from && !receiptDateRange.to}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelReceiptDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyReceiptDateRange}
                    disabled={
                      !tempPaymentDateRange.from || !tempPaymentDateRange.to
                    }
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Menu>

          {/* Reset Button */}
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: "30px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          {/* Add Receipt Button */}
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              onClick={() => navigate("/dashboard/receipt-voucher-create")}
              sx={{
                height: 30,
                display: "flex",
                alignItems: "center",
                fontSize: "0.7rem",
                borderRadius: "0",
              }}
              fullWidth
            >
              <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Receipt
            </Button>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportExcel}
              disabled={isExporting || receiptVoucher.length === 0}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              {isExporting ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 0.5 }} />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            "& .MuiTypography-root": {
              fontSize: "0.7rem",
            },
          },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setSearch((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max:
                    tempCustomDateRange.to ||
                    new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>
<Box sx={{ position: "relative", flex: 1, minHeight: "400px" }}>
  
  {/* ✅ LOADING OVERLAY - Only shows for filter changes */}
  {filterLoading && (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 1000,
        borderRadius: 1,
      }}
    >
      <CircularProgress size={50} thickness={4} color="primary" />
      <Typography sx={{ mt: 2, color: "#333", fontWeight: 500 }}>
        {search.date_range === "financial_year"
          ? "Loading Financial Year Data..."
          : search.date_range === "current_year"
          ? "Loading Current Year Data..."
          : search.date_range === "this_month"
          ? "Loading This Month Data..."
          : search.date_range === "this_week"
          ? "Loading This Week Data..."
          : search.date_range === "previous_week"
          ? "Loading Previous Week Data..."
          : search.date_range === "custom"
          ? "Loading Custom Date Range..."
          : search.receipt_date_from && search.receipt_date_to
          ? "Loading Receipt Date Range..."
          : "Loading Data..."}
      </Typography>
      <Typography sx={{ mt: 0.5, color: "#666", fontSize: "0.8rem" }}>
        Please wait, this may take a moment
      </Typography>
    </Box>
  )}

      {/* InfiniteScroll with Mobile/Desktop Views */}
      <InfiniteScroll
        dataLength={receiptVoucher.length}
        next={fetchMoreData}
        hasMore={hasMore}
        scrollableTarget={isMobile ? undefined : "scrollableDiv"}
        loader={<h4>Loading...</h4>}
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW */
          <Box
            sx={{
              bgcolor: "background.paper",
              overflow: "auto",
            }}
          >
            {receiptVoucher.map((voucher, index) => (
              <Box key={voucher.id || index}>
                <Box
                  onClick={() =>
                    setExpandedReceipt(
                      expandedReceipt === voucher.receipt_voucher_no
                        ? null
                        : voucher.receipt_voucher_no
                    )
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:active": {
                      bgcolor: "#e3f2fd",
                    },
                    borderLeft: `4px solid ${
                      voucher.status === "Approved"
                        ? "#4caf50"
                        : voucher.status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                    }`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#666",
                          minWidth: 25,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          flex: 1,
                        }}
                      >
                        {voucher.receipt_number}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#1976d2",
                        }}
                      >
                        ₹{Number(voucher.rec_amt || 0).toLocaleString("en-IN")}
                      </Typography>
                      <Chip
                        label={voucher.payment_mode}
                        color="primary"
                        size="small"
                        sx={{
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedReceipt === voucher.receipt_voucher_no ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <Collapse
                  in={expandedReceipt === voucher.receipt_voucher_no}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                      borderLeft: `4px solid ${
                        voucher.status === "Approved"
                          ? "#4caf50"
                          : voucher.status === "Rejected"
                          ? "#f44336"
                          : "#ff9800"
                      }`,
                    }}
                  >
                    {/* Amount Box */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 0.8,
                        mb: 1,
                        p: 1,
                        bgcolor: "#e3f2fd",
                        borderRadius: "4px",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#1976d2",
                            letterSpacing: "0.5px",
                          }}
                        >
                          RECEIVED AMOUNT
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: "#1976d2",
                          }}
                        >
                          ₹
                          {Number(voucher.rec_amt || 0).toLocaleString("en-IN")}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Details Grid */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DEBTOR
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {voucher.company_name || "N/A"}
                        </Typography>
                      </Box>

                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PROJECT
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {voucher.project_name || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DIVISION
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.division_name || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PI NO
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.pi_no || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          SALES VOUCHER NO
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.sales_number || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          INVOICE NUMBER
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.invoice_number || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          INVOICE DATE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {new Date(voucher.invoice_date).toLocaleDateString(
                            "en-GB"
                          )}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PAYMENT MODE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.payment_mode || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          RECEIPT DATE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.receipt_date || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          CREATED BY
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.created_by_name || "N/A"}
                        </Typography>
                      </Box>

                      {voucher.approved_name && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            APPROVED BY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {voucher.approved_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {voucher.remarks && (
                      <Box sx={{ mb: 0.8 }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                            mb: 0.3,
                          }}
                        >
                          REMARKS
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#555",
                            fontStyle: "italic",
                            lineHeight: 1.3,
                          }}
                        >
                          {voucher.remarks}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {userRole != "Staff" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            bgcolor:
                              voucher.status === "Approved"
                                ? "#e8f5e9"
                                : voucher.status === "Rejected"
                                ? "#ffebee"
                                : "#fff3e0",
                            px: 1,
                            py: 0.5,
                            borderRadius: "6px",
                            border: `1px solid ${
                              voucher.status === "Approved"
                                ? "#4caf50"
                                : voucher.status === "Rejected"
                                ? "#f44336"
                                : "#ff9800"
                            }`,
                          }}
                        >
                          <Checkbox
                            checked={
                              voucher.status === "Approved" ||
                              voucher.status === "Rejected"
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              setApproveAnchorEl(e.currentTarget);
                              setPurchaseToApprove(voucher);
                            }}
                            sx={{
                              padding: 0,
                              transform: "scale(0.75)",
                              color:
                                voucher.status === "Approved"
                                  ? "#4caf50"
                                  : voucher.status === "Rejected"
                                  ? "#f44336"
                                  : "#ff9800",
                              "&.Mui-checked": {
                                color:
                                  voucher.status === "Approved"
                                    ? "#4caf50"
                                    : "#f44336",
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              color:
                                voucher.status === "Approved"
                                  ? "#2e7d32"
                                  : voucher.status === "Rejected"
                                  ? "#c62828"
                                  : "#f57c00",
                            }}
                          >
                            {voucher.status === "Approved"
                              ? "Approved"
                              : voucher.status === "Rejected"
                              ? "Rejected"
                              : "Approve"}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(voucher.receipt_voucher_no);
                          }}
                          disabled={voucher.status === "Approved"}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#bbdefb",
                              transform: "scale(1.05)",
                            },
                            "&:disabled": {
                              bgcolor: "#f5f5f5",
                              opacity: 0.5,
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Edit
                            sx={{
                              fontSize: "1rem",
                              color: "#1976d2",
                              transform: "scale(0.6)",
                            }}
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setVoucherToDelete(voucher.receipt_voucher_no);
                          }}
                          disabled={voucher.status === "Approved"}
                          sx={{
                            bgcolor: "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                            "&:disabled": {
                              bgcolor: "#f5f5f5",
                              opacity: 0.5,
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Delete
                            sx={{
                              fontSize: "1rem",
                              color: "#d32f2f",
                              transform: "scale(0.6)",
                            }}
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateReceiptPdf(voucher);
                          }}
                          sx={{
                            bgcolor: "#fff3e0",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffe0b2",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Print
                            sx={{
                              fontSize: "1rem",
                              color: "#f57c00",
                              transform: "scale(0.6)",
                            }}
                          />
                        </IconButton>

                        {typeof voucher.attachment === "string" ? (
                          <a
                            href={`${voucher.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: "#e8f5e9",
                                borderRadius: "6px",
                                width: 32,
                                height: 32,
                                "&:hover": {
                                  bgcolor: "#c8e6c9",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <Visibility
                                sx={{
                                  fontSize: "1rem",
                                  color: "#2e7d32",
                                  transform: "scale(0.6)",
                                }}
                              />
                            </IconButton>
                          </a>
                        ) : voucher.attachment instanceof File ? (
                          <a
                            href={URL.createObjectURL(voucher.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: "#e8f5e9",
                                borderRadius: "6px",
                                width: 32,
                                height: 32,
                                "&:hover": {
                                  bgcolor: "#c8e6c9",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <Visibility
                                sx={{
                                  fontSize: "1rem",
                                  color: "#2e7d32",
                                  transform: "scale(0.6)",
                                }}
                              />
                            </IconButton>
                          </a>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                <Divider />
              </Box>
            ))}

            {/* Mobile Total Summary */}
            <Box
              sx={{
                p: 2,
                bgcolor: "#e3f2fd",
                position: "sticky",
                bottom: 0,
                borderTop: "2px solid #1976d2",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  Total Received:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  ₹
                  {receiptVoucher
                    .reduce((sum, v) => sum + parseFloat(v.rec_amt || 0), 0)
                    .toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - SAME AS BEFORE */
          <TableContainer
            component={Paper}
            id="scrollableDiv"
            sx={{
              maxHeight: "88vh",
              overflow: "auto",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1600,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                      fontSize: "0.7rem",
                    },
                  }}
                >
                  {userRole != "Staff" && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fafafa",
                        zIndex: 3,
                      }}
                    >
                      Approve
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 80 : 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S.No.
                  </TableCell>

                  <TableCell>Voucher No</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Debtor</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Rec Amt</TableCell>
                  <TableCell>PI No</TableCell>
                  <TableCell>Sales voucher No</TableCell>
                  <TableCell>Invoice number</TableCell>
                  <TableCell>Invoice Date</TableCell>
                  <TableCell>Payment Mode</TableCell>
                  <TableCell>Receipt Date</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {receiptVoucher.map((voucher, index) => (
                  <TableRow
                    hover
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                      },
                    }}
                  >
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fafafa",
                          zIndex: 2,
                        }}
                      >
                        <Checkbox
                          checked={
                            voucher.status === "Approved" ||
                            voucher.status === "Rejected"
                          }
                          onChange={(e) => {
                            setApproveAnchorEl(e.currentTarget);
                            setPurchaseToApprove(voucher);
                          }}
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                            transform: "scale(0.6)",
                            color:
                              voucher.status === "Approved"
                                ? "green"
                                : voucher.status === "Rejected"
                                ? "red"
                                : "grey",
                            "&.Mui-checked": {
                              color:
                                voucher.status === "Approved" ? "green" : "red",
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 80 : 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 2,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell>{voucher.receipt_number}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          voucher.status === "Approved"
                            ? "Approved"
                            : voucher.status === "Rejected"
                            ? "Rejected"
                            : "Pending"
                        }
                        color={
                          voucher.status === "Approved"
                            ? "success"
                            : voucher.status === "Rejected"
                            ? "error"
                            : "warning"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={voucher.company_name || ""} arrow>
                        <span>
                          {truncateText(voucher.company_name || "", 20)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{voucher.project_name}</TableCell>
                    <TableCell>{voucher.division_name}</TableCell>
                    <TableCell>
                      {voucher.rec_amt?.toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell>{voucher.pi_no}</TableCell>
                    <TableCell>{voucher.sales_number}</TableCell>
                    <TableCell>{voucher.invoice_number}</TableCell>
                    <TableCell>
                      {new Date(voucher.invoice_date).toLocaleDateString(
                        "en-GB"
                      )}
                    </TableCell>
                    <TableCell>{voucher.payment_mode}</TableCell>
                    <TableCell>{voucher.receipt_date}</TableCell>
                    <TableCell>{voucher.created_by_name}</TableCell>
                    <TableCell>{voucher.approved_name}</TableCell>
                    <TableCell>
                      <Tooltip title={voucher.remarks || ""} arrow>
                        <span>{truncateText(voucher.remarks || "", 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 2,
                        minWidth: 180,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleEditClick(voucher.receipt_voucher_no)
                          }
                          disabled={voucher.status === "Approved"}
                        >
                          <Edit transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          size="small"
                          color="error"
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setVoucherToDelete(voucher.receipt_voucher_no);
                          }}
                          disabled={voucher.status === "Approved"}
                        >
                          <Delete transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          color="warning"
                          size="small"
                          onClick={() => generateReceiptPdf(voucher)}
                        >
                          <Print transform="scale(0.6)" />
                        </IconButton>
                        <span
                          style={{
                            display: "inline-block",
                            width: 36,
                            textAlign: "center",
                          }}
                        >
                          {typeof voucher.attachment === "string" ? (
                            <a
                              href={`${voucher.attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton
                                sx={{
                                  padding: 0,
                                  paddingLeft: 1,
                                  paddingRight: 1,
                                }}
                                color="info"
                                size="small"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </a>
                          ) : voucher.attachment instanceof File ? (
                            <a
                              href={URL.createObjectURL(voucher.attachment)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton
                                sx={{
                                  padding: 0,
                                  paddingLeft: 1,
                                  paddingRight: 1,
                                }}
                                color="info"
                                size="small"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </a>
                          ) : (
                            <span
                              style={{ display: "inline-block", width: 24 }}
                            />
                          )}
                        </span>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{
                    "& td": {
                      backgroundColor: "#E3F2FD !important",
                    },
                  }}
                >
                  <TableCell
                    colSpan={7}
                    align="right"
                    sx={{ fontWeight: "bold !important" }}
                  >
                    Totals:
                  </TableCell>
                  <TableCell>
                    {receiptVoucher
                      .reduce((sum, v) => sum + parseFloat(v.rec_amt || 0), 0)
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}
                  </TableCell>
                  <TableCell colSpan={10} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
      </Box>

      {/* Popovers - Same as before */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setVoucherToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                await handleDelete(voucherToDelete);
                setAnchorEl(null);
                setVoucherToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setVoucherToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

     <Popover
  open={Boolean(approveAnchorEl)}
  anchorEl={approveAnchorEl}
  onClose={() => {
    setApproveAnchorEl(null);
    setPurchaseToApprove(null);
    setRemarkInput("");
    setShowRemarkForIndentId(null);
    setActionType(null);
  }}
  anchorReference="anchorPosition"
  anchorPosition={{
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
  }}
  transformOrigin={{
    vertical: "center",
    horizontal: "center",
  }}
  PaperProps={{
    sx: {
      width: { xs: "90%", sm: "95%", md: "90%", lg: 1100 },
      maxWidth: { xs: "90%", sm: "95%", md: "90%", lg: 1100 },
      maxHeight: { xs: "85vh", sm: "90vh" },
      height: { xs: "auto", sm: "auto" },
      borderRadius: { xs: 2, sm: 2 },
      m: { xs: 0, sm: 2 },
      overflow: "auto",
      position: { xs: "fixed", sm: "relative" },
      top: { xs: "50%", sm: "auto" },
      left: { xs: "50%", sm: "auto" },
      transform: { xs: "translate(-50%, -50%)", sm: "none" },
    },
  }}
  slotProps={{
    backdrop: {
      sx: {
        backgroundColor: { xs: "rgba(0, 0, 0, 0.7)", sm: "rgba(0, 0, 0, 0.5)" },
      },
    },
  }}
>
  <Box
    sx={{
      p: { xs: 2, sm: 2 },
      minWidth: { xs: "auto", sm: 500, md: 700, lg: 1050 },
    }}
  >
    {/* Header */}
    <Box
      sx={{
        mb: { xs: 2, sm: 2 },
        pb: { xs: 1.5, sm: 0 },
        borderBottom: { xs: "1px solid #e0e0e0", sm: "none" },
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: { xs: "1.1rem", sm: "0.9rem", md: "1rem" },
          fontWeight: 600,
        }}
      >
        Receipt Voucher Details
      </Typography>
    </Box>

    {/* ===== MOBILE VIEW - Card Style ===== */}
    <Box sx={{ display: { xs: "block", sm: "none" } }}>
      {/* Main Details Card */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.2,
          p: 2,
          bgcolor: "#f5f5f5",
          borderRadius: 1,
          mb: 2,
        }}
      >
        {/* Voucher No */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Voucher No.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: "#1a237e",
              fontWeight: 500,
            }}
          >
            {PurchaseToApprove?.receipt_number || "-"}
          </Typography>
        </Box>

        {/* Created Date */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Created Date
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: "#1a237e",
              fontWeight: 500,
            }}
          >
            {PurchaseToApprove?.created_at
              ? dayjs(PurchaseToApprove.created_at).format("DD/MM/YYYY")
              : "-"}
          </Typography>
        </Box>

        {/* Receipt Amount */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Receipt Amt
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: "#2e7d32",
              fontWeight: 600,
            }}
          >
            ₹{PurchaseToApprove?.rec_amt || "0"}
          </Typography>
        </Box>

        {/* Invoice No */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Invoice No
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            {PurchaseToApprove?.invoice_number || "-"}
          </Typography>
        </Box>

        {/* Receipt Date */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Receipt Date
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            {PurchaseToApprove?.receipt_date
              ? dayjs(PurchaseToApprove.receipt_date).format("DD/MM/YYYY")
              : "-"}
          </Typography>
        </Box>

        {/* Debtor */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
              flexShrink: 0,
            }}
          >
            Debtor
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {PurchaseToApprove?.company_name || "-"}
          </Typography>
        </Box>

        {/* PI No */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            PI No
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            {PurchaseToApprove?.pi_no || "-"}
          </Typography>
        </Box>

        {/* Project Name */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
              flexShrink: 0,
            }}
          >
            Project Name
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {PurchaseToApprove?.project_name || "-"}
          </Typography>
        </Box>
      </Box>

      {/* Payment Details Card */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.2,
          p: 2,
          bgcolor: "#fafafa",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Payment Mode */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Payment Mode
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            {PurchaseToApprove?.payment_mode || "-"}
          </Typography>
        </Box>

        {/* Status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Status
          </Typography>
          <Chip
            label={PurchaseToApprove?.status || "Pending"}
            size="small"
            color={
              PurchaseToApprove?.status === "Approved"
                ? "success"
                : PurchaseToApprove?.status === "Rejected"
                ? "error"
                : "warning"
            }
            sx={{ fontSize: "0.75rem", height: 24 }}
          />
        </Box>

        {/* Account No */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Account No.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            {PurchaseToApprove?.account_no || "-"}
          </Typography>
        </Box>

        {/* Voucher Type */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
            }}
          >
            Voucher Type
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            {PurchaseToApprove?.voucher_type || "-"}
          </Typography>
        </Box>

        {/* Narration */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: "0.8rem",
              color: "#333",
              mb: 0.5,
            }}
          >
            Narration
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              wordBreak: "break-word",
              bgcolor: "#fff",
              p: 1.5,
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            {PurchaseToApprove?.narration || "-"}
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ===== DESKTOP/TABLET VIEW - Table Style ===== */}
    <Box sx={{ display: { xs: "none", sm: "block" }, overflowX: "auto" }}>
      <Table
        size="small"
        sx={{
          border: "1px solid #000",
          minWidth: { sm: 500, md: 700, lg: 1000 },
          "& td": {
            borderBottom: "none",
            padding: {
              sm: "4px 8px",
              md: "6px 10px",
              lg: "8px 12px",
            },
            fontSize: {
              sm: "0.75rem",
              md: "0.8rem",
              lg: "0.85rem",
            },
          },
        }}
      >
        <TableBody>
          {/* ===== ROW 1 ===== */}
          <TableRow>
            <TableCell sx={{ width: "12%" }}>
              <b>Voucher No.</b>
            </TableCell>
            <TableCell sx={{ width: "18%", wordBreak: "break-word" }}>
              : {PurchaseToApprove?.receipt_number}
            </TableCell>

            <TableCell sx={{ width: "12%" }}>
              <b>Created Date</b>
            </TableCell>
            <TableCell sx={{ width: "18%" }}>
              :{" "}
              {PurchaseToApprove?.created_at
                ? dayjs(PurchaseToApprove.created_at).format("DD/MM/YYYY")
                : "-"}
            </TableCell>

            <TableCell sx={{ width: "12%" }}>
              <b>Receipt Amt</b>
            </TableCell>
            <TableCell
              sx={{ width: "18%", color: "#2e7d32", fontWeight: 600 }}
            >
              : ₹{PurchaseToApprove?.rec_amt}
            </TableCell>
          </TableRow>

          {/* ===== ROW 2 ===== */}
          <TableRow>
            <TableCell>
              <b>Invoice No</b>
            </TableCell>
            <TableCell sx={{ wordBreak: "break-word" }}>
              : {PurchaseToApprove?.invoice_number}
            </TableCell>

            <TableCell>
              <b>Receipt Date</b>
            </TableCell>
            <TableCell>
              :{" "}
              {PurchaseToApprove?.receipt_date
                ? dayjs(PurchaseToApprove.receipt_date).format("DD/MM/YYYY")
                : "-"}
            </TableCell>

            <TableCell>
              <b>Debtor</b>
            </TableCell>
            <TableCell sx={{ wordBreak: "break-word" }}>
              : {PurchaseToApprove?.company_name}
            </TableCell>
          </TableRow>

          {/* ===== ROW 3 ===== */}
          <TableRow>
            <TableCell>
              <b>PI No</b>
            </TableCell>
            <TableCell>: {PurchaseToApprove?.pi_no || "-"}</TableCell>

            <TableCell>
              <b>Project Name</b>
            </TableCell>
            <TableCell sx={{ wordBreak: "break-word" }} colSpan={3}>
              : {PurchaseToApprove?.project_name}
            </TableCell>
          </TableRow>

          {/* ===== SEPARATOR ===== */}
          <TableRow>
            <TableCell colSpan={6} sx={{ borderTop: "2px solid #000" }} />
          </TableRow>

          {/* ===== ROW 4 ===== */}
          <TableRow>
            <TableCell>
              <b>Payment Mode</b>
            </TableCell>
            <TableCell>: {PurchaseToApprove?.payment_mode}</TableCell>

            <TableCell>
              <b>Status</b>
            </TableCell>
            <TableCell colSpan={3}>
              :{" "}
              <Chip
                label={PurchaseToApprove?.status || "Pending"}
                size="small"
                color={
                  PurchaseToApprove?.status === "Approved"
                    ? "success"
                    : PurchaseToApprove?.status === "Rejected"
                    ? "error"
                    : "warning"
                }
                sx={{ fontSize: "0.7rem", height: 22, ml: 1 }}
              />
            </TableCell>
          </TableRow>

          {/* ===== ROW 5 ===== */}
          <TableRow>
            <TableCell>
              <b>Account No.</b>
            </TableCell>
            <TableCell>: {PurchaseToApprove?.account_no || "-"}</TableCell>

            <TableCell>
              <b>Voucher Type</b>
            </TableCell>
            <TableCell colSpan={3}>
              : {PurchaseToApprove?.voucher_type}
            </TableCell>
          </TableRow>

          {/* ===== ROW 6 ===== */}
          <TableRow>
            <TableCell>
              <b>Narration</b>
            </TableCell>
            <TableCell colSpan={5} sx={{ wordBreak: "break-word" }}>
              : {PurchaseToApprove?.narration}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>

    <Divider sx={{ my: { xs: 2, sm: 2 } }} />

    {/* ===== REMARK INPUT SECTION ===== */}
    {showRemarkForIndentId === PurchaseToApprove?.receipt_voucher_no ? (
      <Box sx={{ mt: { xs: 2, sm: 2 } }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            fontSize: { xs: "0.9rem", sm: "0.9rem" },
            fontWeight: 500,
          }}
        >
          Please enter remark before{" "}
          {actionType === "Approved" ? "approving" : "rejecting"}:
        </Typography>
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          value={remarkInput}
          onChange={(e) => setRemarkInput(e.target.value)}
          placeholder="Enter remark"
          sx={{
            "& .MuiInputBase-input": {
              fontSize: { xs: "0.9rem", sm: "0.9rem" },
            },
          }}
        />

        {/* Yes/No Buttons - 2 columns on mobile, inline on desktop */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto" },
            justifyContent: { xs: "stretch", sm: "flex-end" },
            gap: { xs: 1.5, sm: 1 },
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            color={actionType === "Approved" ? "success" : "error"}
            size="small"
            onClick={() => {
              if (!remarkInput.trim()) {
                toast.error("Remark is required!");
                return;
              }
              handleApprove({
                ...PurchaseToApprove,
                status: actionType,
                remarks: remarkInput,
              });
              setApproveAnchorEl(null);
              setPurchaseToApprove(null);
              setRemarkInput("");
              setShowRemarkForIndentId(null);
              setActionType(null);
            }}
            sx={{
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              py: { xs: 1.2, sm: 0.5 },
            }}
          >
            Yes, {actionType === "Approved" ? "Approve" : "Reject"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setApproveAnchorEl(null);
              setPurchaseToApprove(null);
              setRemarkInput("");
              setShowRemarkForIndentId(null);
              setActionType(null);
            }}
            sx={{
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              py: { xs: 1.2, sm: 0.5 },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    ) : (
      <Box sx={{ mt: { xs: 2, sm: 2 } }}>
        <Typography
          variant="body2"
          sx={{
            mb: { xs: 2, sm: 1 },
            fontSize: { xs: "0.9rem", sm: "0.9rem" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Do you want to approve or reject this Receipt Voucher?
        </Typography>

        {/* Action Buttons - 2x2 Grid on Mobile, Inline on Desktop */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto auto auto" },
            justifyContent: { xs: "stretch", sm: "flex-end" },
            gap: { xs: 1.5, sm: 1 },
          }}
        >
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              setShowRemarkForIndentId(
                PurchaseToApprove?.receipt_voucher_no
              );
              setActionType("Approved");
            }}
            sx={{
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              py: { xs: 1.2, sm: 0.5 },
            }}
          >
            Approve
          </Button>

          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              setShowRemarkForIndentId(
                PurchaseToApprove?.receipt_voucher_no
              );
              setActionType("Rejected");
            }}
            disabled={PurchaseToApprove?.status === "Approved"}
            sx={{
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              py: { xs: 1.2, sm: 0.5 },
            }}
          >
            Reject
          </Button>

          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() =>
              handleEditClick(PurchaseToApprove.receipt_voucher_no)
            }
            disabled={PurchaseToApprove?.status === "Approved"}
            sx={{
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              py: { xs: 1.2, sm: 0.5 },
            }}
          >
            Edit
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setApproveAnchorEl(null);
              setPurchaseToApprove(null);
              setRemarkInput("");
              setShowRemarkForIndentId(null);
              setActionType(null);
            }}
            sx={{
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              py: { xs: 1.2, sm: 0.5 },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    )}
  </Box>
</Popover>
    </>
  );
};

export default ReceiptVoucher;
